import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './Checkout.css';
import axios from 'axios';
import { API_BASE_URL } from '../utils';
import iziToast from 'izitoast';

const Checkout = () => {
    const [paymentGateway, setPaymentGateway] = useState('razorpay');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);

    const [searchParams] = useSearchParams();
    const sourcePage = searchParams.get("sourcePage");

    const { orderId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const { orderData, useCarrot, selectedCoupon, amounts } = location.state || {};

    useEffect(() => {
        if (!orderData || !orderId) {
            navigate(-1);
            return;
        }

        setOrderDetails({
            orderId,
            orderData,
            useCarrot: useCarrot || false,
            selectedCoupon: selectedCoupon || null,
            amounts
        });
    }, [orderData, orderId, navigate, useCarrot, selectedCoupon, amounts]);

    const verifyRazorpayPayment = useCallback(async (razorpayResponse) => {
        try {
            const formdata = new FormData();
            formdata.append("response", JSON.stringify(razorpayResponse));

            const url = sourcePage === "table"
                ? `${API_BASE_URL}/master/APIs/ClubTable/verifyRzpPayment`
                : `${API_BASE_URL}/master/APIs/ClubPackage/verifyRzpPayment`;

            const response = await axios.post(url, formdata);
            const verificationResponse = response.data;

            if (verificationResponse.status) {
                iziToast.success({
                    title: 'Success',
                    message: verificationResponse.msg,
                    timeout: 5000,
                    position: 'topCenter',
                });
                return true;
            } else {
                iziToast.error({
                    title: 'Error',
                    message: verificationResponse.msg,
                    timeout: 5000,
                    position: 'topCenter',
                });
                return false;
            }
        } catch (error) {
            console.error("Error verifying payment:", error);
            iziToast.error({
                title: 'Error',
                message: 'An error occurred while verifying the payment.',
                timeout: 5000,
                position: 'topCenter',
            });
            return false;
        }
    }, [sourcePage]);

    const handleCheckout = async () => {
        if (!orderDetails) {
            setError('Order details not found');
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('order_id', orderData.order_id);
            formData.append('carrot_use', orderDetails.useCarrot ? 'true' : 'false');
            formData.append('coupon_code', orderDetails.selectedCoupon?.code || '');
            formData.append('payment_gateway', paymentGateway);

            const url = sourcePage === "table"
                ? `${API_BASE_URL}/master/APIs/ClubTable/checkout`
                : `${API_BASE_URL}/master/APIs/ClubPackage/checkout`;

            const response = await axios.post(url, formData);
            const responseData = response.data;

            console.log('Checkout response:', responseData);

            if (responseData.status === true) {
                if (paymentGateway === 'razorpay' && responseData.data) {
                    initializeRazorpay(responseData.data);
                } else {
                    if (responseData.redirectUrl) {
                        window.location.href = responseData.redirectUrl;
                    } else {
                        setError("Invalid payment data received for Razorpay.");
                    }
                }
            } else {
                setError(responseData.msg || 'Payment initiation failed');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            if (error.code === 'ECONNABORTED') {
                setError('Request timeout. Please try again.');
            } else if (error.response) {
                setError(`Server error: ${error.response.data?.msg || error.response.status}`);
            } else if (error.request) {
                setError('Network error. Please check your connection.');
            } else {
                setError('An unexpected error occurred.');
            }
        } finally {
            setProcessing(false);
        }
    };

    const initializeRazorpay = (paymentData) => {
        if (!window.Razorpay) {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => openRazorpay(paymentData);
            script.onerror = () => setError('Failed to load Razorpay. Please try again.');
            document.body.appendChild(script);
        } else {
            openRazorpay(paymentData);
        }
    };

    const openRazorpay = (paymentData) => {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');

        console.log('Payment Data:', paymentData);

        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY,
            amount: paymentData.amount * 100,
            currency: 'INR',
            name: 'PartyWitty',
            description: `PartyWitty Booking - Order ${orderDetails.orderId}`,
            order_id: paymentData.rzp_order_id,
            prefill: {
                name: userData.name || '',
                email: userData.email || '',
                contact: paymentData.userMobile || userData.mobile || ''
            },
            theme: {
                color: '#FF6B6B'
            },
            modal: {
                ondismiss: () => {
                    setProcessing(false);
                    setError('Payment cancelled by user');
                }
            },
            handler: async function (response) {
                try {
                    const verified = await verifyRazorpayPayment(response);
                    if (verified) {
                        navigate(`/payment-done/${orderDetails.orderId}`, {
                            state: {
                                orderId: orderDetails.orderId,
                                transactionId: response.razorpay_payment_id,
                                razorpayOrderId: response.razorpay_order_id,
                                signature: response.razorpay_signature
                            }
                        });
                    } else {
                        setError("Payment verification failed. Please contact support.");
                    }
                } catch (error) {
                    console.error("Payment verification error:", error);
                    setError("Something went wrong during payment verification.");
                }
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (response) => {
            console.error('Razorpay payment failed:', response);
            setError(`Payment failed: ${response.error.description}`);
            setProcessing(false);
        });

        rzp.open();
    };

    const calculateFinalAmount = () => {
        if (!orderDetails?.amounts) return 0;
        return orderDetails.amounts.finalPayableAmount || orderDetails.amounts.netPayableAmount || 0;
    };


    if (!orderDetails) {
        return (
            <div className="checkout-page">
                <div className="loading-container">
                    <p>Loading checkout details...</p>
                </div>
            </div>
        );
    }

    const finalAmount = calculateFinalAmount();

    return (
        <div className='checkout-page-section'>
            <div className="checkout-page">
                <div className="checkout-container">
                    {/* Header */}
                    <div className="checkout-header">
                        <button
                            className="checkout-back-btn"
                            onClick={() => navigate(-1)}
                            disabled={processing}
                        >
                            ← Back
                        </button>
                        <h1>Complete Payment</h1>
                    </div>

                    {/* Order Summary */}
                    {/* <div className="checkout-order-summary">
                        <h2>Order Summary</h2>
                        <div className="order-info">
                            <div className="booking-summary">
                                <div className="summary-row">
                                    <span>Party Date:</span>
                                    <span>{orderDetails.orderData.party_date}</span>
                                </div>
                                <div className="summary-row">
                                    <span>Party Time:</span>
                                    <span>{orderDetails.orderData.party_time}</span>
                                </div>
                                <div className="summary-row">
                                    <span>Total Guests:</span>
                                    <span>
                                        {[
                                            Number(orderDetails.orderData.male_qty),
                                            Number(orderDetails.orderData.female_qty),
                                            Number(orderDetails.orderData.kids_qty)
                                        ].reduce((sum, qty) => sum + (qty || 0), 0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div> */}

                    {/* Payment Breakdown */}
                    <div className="payment-breakdown">
                        <h2>Payment Breakdown</h2>
                        <div className="breakdown-details">
                            <div className="breakdown-row">
                                <span>Base Amount</span>
                                <span>₹{orderDetails.amounts?.netPayableAmount?.toFixed(2) || '0.00'}</span>
                            </div>

                            {orderDetails.useCarrot && orderDetails.amounts?.carrotDiscount > 0 && (
                                <div className="breakdown-row discount">
                                    <span>🥕 Carrot Discount</span>
                                    <span>-₹{orderDetails.amounts.carrotDiscount.toFixed(2)}</span>
                                </div>
                            )}

                            {orderDetails.selectedCoupon && orderDetails.amounts?.couponDiscount > 0 && (
                                <div className="breakdown-row discount">
                                    <span>🎫 Coupon ({orderDetails.selectedCoupon.code})</span>
                                    <span>-₹{orderDetails.amounts.couponDiscount.toFixed(2)}</span>
                                </div>
                            )}

                            <div className="breakdown-row total">
                                <strong>Amount to Pay</strong>
                                <strong>₹{finalAmount.toFixed(2)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* Payment Gateway Selection */}
                    <div className="payment-gateway-section">
                        <h2>Select Payment Method</h2>
                        <div className="payment-options">
                            {/* <label className="payment-option">
                                <input
                                    type="radio"
                                    value="phonepe"
                                    checked={paymentGateway === 'phonepe'}
                                    onChange={(e) => setPaymentGateway(e.target.value)}
                                    disabled={processing}
                                />
                                <div className="payment-method">
                                    <span className="payment-icon">📱</span>
                                    <span>PhonePe</span>
                                </div>
                            </label> */}

                            <label className="payment-option">
                                <input
                                    type="radio"
                                    value="razorpay"
                                    checked={paymentGateway === 'razorpay'}
                                    onChange={(e) => setPaymentGateway(e.target.value)}
                                    disabled={processing}
                                />
                                <div className="payment-method">
                                    <span className="payment-icon">💳</span>
                                    <span>Razorpay (Cards, UPI, Net Banking)</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="error-message">
                            <span className="error-icon">⚠️</span>
                            <p>{error}</p>
                        </div>
                    )}

                    {/* Pay Now Button */}


                    {/* Security Info */}
                    <div className="security-info">
                        <p>
                            🔒 Your payment information is secure and encrypted.
                            We use industry-standard security measures to protect your data.
                        </p>
                    </div>
                </div>
            </div>
            <div className="Checkout-payment-footer-section">
                <div className='Checkout-payment-footer-wrapper'>

                    <div>
                        <h2>₹{finalAmount.toFixed(2)}</h2>
                    </div>
                    <div className="payment-action">
                        <button
                            className="pay-now-btn"
                            onClick={handleCheckout}
                            disabled={processing || finalAmount <= 0}
                        >
                            {processing ? (
                                <>
                                    {/* <span className="loading-spinner"></span> */}
                                    Processing...
                                </>
                            ) : (
                                // `Pay ₹${finalAmount.toFixed(2)} Now`
                                `Pay Now`
                            )}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Checkout;